import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { ArrowLeft, ChevronDown, ChevronRight, BarChart3, AlertTriangle, ChevronUp, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../api/examApi';
import { DASH, fmtPct, fmtAge, fmtMinutes, pctTone } from '../lib/reportFormat';
import { InfoTooltip, Th, HowToReadPanel } from '../components/reports/ReportTooltip';

const todayIso = () => new Date().toISOString().slice(0, 10);

// §12.3 row header dual count — composed here from the two raw numeric
// fields, never in the service. Single figure when the two agree, dual
// figure ("N active · M who sat tests this period") only when they differ —
// matches the backend's own dual_count definition text exactly.
function dualCountLabel(activeMembers, studentsWithWork) {
  if (activeMembers === studentsWithWork) {
    return `${activeMembers} active`;
  }
  return `${activeMembers} active · ${studentsWithWork} who sat tests this period`;
}

// §1.1 three-state punctuality bar, adapted for the drill-down's
// {on_time, late, no_upload} shape (no separate "assigned" field — the
// three counts already sum to the group's total).
//
// §12.6 emphasis — twoPlusNoUpload is colour/weight only on the existing
// "none" figure (no badge, no added text, no advice): true when at least
// one student in this group missed 2+ of this faculty's exams entirely.
function SubmissionsBar({ on_time, late, no_upload, twoPlusNoUpload, definitions }) {
  const total = on_time + late + no_upload;
  if (total === 0) {
    return <span className="text-xs text-slate-400">{DASH}</span>;
  }
  const pct = (n) => (100 * n) / total;
  return (
    <div className="w-40">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
        {on_time > 0 && <div style={{ width: `${pct(on_time)}%` }} className="bg-emerald-500" title={`${on_time} on time`} />}
        {late > 0 && <div style={{ width: `${pct(late)}%` }} className="bg-amber-500" title={`${late} late`} />}
        {no_upload > 0 && <div style={{ width: `${pct(no_upload)}%` }} className="bg-slate-300 dark:bg-slate-500" title={`${no_upload} none`} />}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-2 text-[11px] text-slate-500 dark:text-slate-400">
        <span><span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1" />{on_time} on-time</span>
        <span><span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 mr-1" />{late} late</span>
        <span className={twoPlusNoUpload ? 'inline-flex items-center gap-1 font-semibold text-rose-600 dark:text-rose-400' : 'inline-flex items-center gap-1'}>
          <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1 ${twoPlusNoUpload ? 'bg-rose-500' : 'bg-slate-400'}`} />
          {no_upload} none
          {twoPlusNoUpload && <InfoTooltip id="two_plus_no_upload" text={definitions ? definitions.two_plus_no_upload : undefined} />}
        </span>
      </div>
    </div>
  );
}

// One row-building path for a real group AND the §12.8 individuals
// pseudo-group — same component, same cells, matching the backend's own
// _drilldown_row_for() which produces both from one function.
function GroupTableRow({ row, definitions, slaDays }) {
  // §12.6 emphasis — colour and weight only, no badges/flags/advice.
  const belowFifty = !row.no_tests_in_period && row.class_average.avg_pct !== null && row.class_average.avg_pct < 50;
  const attendanceGap = row.days_since_last_attendance !== null && row.days_since_last_attendance !== undefined && row.days_since_last_attendance >= 7;

  return (
    <tr className="align-top" data-testid={`drilldown-group-row-${row.group_id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-slate-900 dark:text-white">{row.group_name}</p>
          {!row.is_active && (
            <span
              className="inline-flex items-center rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 text-[11px] font-medium"
              data-testid={`inactive-marker-${row.group_id}`}
            >
              inactive
            </span>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-0.5 inline-flex items-center gap-1" data-testid={`dual-count-${row.group_id}`}>
          {dualCountLabel(row.active_members, row.students_with_work)}
          {/* placement="top" — this line sits above the co-taught line in the
              same identity cell; opening downward let a pinned tooltip here
              cover the co-taught line's own info icon underneath it. */}
          <InfoTooltip id={`dual_count-${row.group_id}`} text={definitions.dual_count} placement="top" />
        </p>
        {row.co_taught_by_count > 0 && (
          <p className="text-xs text-slate-400 mt-0.5" data-testid={`co-taught-${row.group_id}`}>
            <span className="inline-flex items-center gap-1">
              Co-taught by {row.co_taught_by_count} other faculty
              <InfoTooltip id={`co_taught_by_count-${row.group_id}`} text={definitions.co_taught_by_count} />
            </span>
            {row.class_average_all_teaching_faculty && (
              <span className="inline-flex items-center gap-1 ml-1">
                &middot; group avg (all teaching faculty): {fmtPct(row.class_average_all_teaching_faculty.avg_pct)} (n={row.class_average_all_teaching_faculty.n})
                <InfoTooltip id={`class_average_all_teaching_faculty-${row.group_id}`} text={definitions.class_average_all_teaching_faculty} />
              </span>
            )}
          </p>
        )}
      </td>

      {/* 1. Tests given to this group */}
      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{row.tests_given}</td>

      {/* 2. Class average */}
      <td className="px-4 py-3">
        {row.no_tests_in_period ? (
          <>
            <span className="text-slate-400">{DASH}</span>
            <p className="text-xs text-slate-400 mt-0.5">no tests in this period</p>
          </>
        ) : (
          <>
            <span className={belowFifty ? 'font-semibold text-rose-600 dark:text-rose-400' : 'font-medium text-slate-800 dark:text-slate-200'}>
              {fmtPct(row.class_average.avg_pct)}
            </span>
            <p className="text-xs text-slate-400 mt-0.5">
              {fmtPct(row.class_average.min_pct, 0)}&ndash;{fmtPct(row.class_average.max_pct, 0)} &middot; n={row.class_average.n}
            </p>
          </>
        )}
      </td>

      {/* 3. Students below 50% */}
      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{row.students_below_50}</td>

      {/* 4. Submissions received */}
      <td className="px-4 py-3">
        <SubmissionsBar
          {...row.submissions_received}
          twoPlusNoUpload={row.students_with_two_plus_no_upload > 0}
          definitions={definitions}
        />
      </td>

      {/* 5. Students assigned */}
      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{row.students_assigned}</td>

      {/* 6. Graded within {slaDays} days (%) */}
      <td className="px-4 py-3">
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${pctTone(row.graded_within_sla.rate_pct)}`}>
          {fmtPct(row.graded_within_sla.rate_pct)}
        </span>
        <p className="text-xs text-slate-400 mt-0.5">n={row.graded_within_sla.graded_n} graded in range</p>
      </td>

      {/* 7. Overdue grading */}
      <td className="px-4 py-3">
        <span className={`font-medium ${row.overdue_grading > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'}`}>
          {row.overdue_grading}
        </span>
        <p className="text-xs text-slate-400">past {slaDays == null ? 'the SLA window' : `${slaDays} days`}, right now</p>
      </td>

      {/* 8. Attendance entered on time (%) */}
      <td className="px-4 py-3">
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${pctTone(row.attendance_on_time.rate_pct)}`}>
          {fmtPct(row.attendance_on_time.rate_pct)}
        </span>
        <p className="text-xs text-slate-400 mt-0.5">
          {row.attendance_on_time.marks > 0 ? `of ${row.attendance_on_time.marks} attendance entries` : 'no attendance entries'}
        </p>
      </td>

      {/* 9. Days since last attendance entry */}
      <td className={`px-4 py-3 ${attendanceGap ? 'font-semibold text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'}`}>
        {row.days_since_last_attendance === null || row.days_since_last_attendance === undefined
          ? DASH
          : `${row.days_since_last_attendance}d`}
      </td>
    </tr>
  );
}

function sortValue(row, key) {
  switch (key) {
    case 'group_name':
      return row.group_name.toLowerCase();
    case 'tests_given':
      return row.tests_given;
    case 'class_average':
      return row.class_average.avg_pct;
    case 'students_below_50':
      return row.students_below_50;
    case 'submissions_received':
      return row.submissions_received.on_time;
    case 'students_assigned':
      return row.students_assigned;
    case 'graded_within_sla':
      return row.graded_within_sla.rate_pct;
    case 'overdue_grading':
      return row.overdue_grading;
    case 'attendance_on_time':
      return row.attendance_on_time.rate_pct;
    case 'days_since_last_attendance':
      return row.days_since_last_attendance;
    default:
      return null;
  }
}

function sortRows(rows, key, dir) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    const aNull = av === null || av === undefined;
    const bNull = bv === null || bv === undefined;
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ? 1 : -1;
    return 0;
  });
  return copy;
}

function SortableHeader({ id, label, sortKey, sortDir, onSort, defId, definitions }) {
  const active = sortKey === id;
  return (
    <th className="px-4 py-3 text-left font-medium text-slate-500">
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => onSort(id)}
          className="inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300"
          data-testid={`sort-${id}`}
        >
          {label}
          {active ? (
            sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
          ) : (
            <ArrowUpDown size={11} className="text-slate-300 dark:text-slate-600" />
          )}
        </button>
        {defId && <InfoTooltip id={defId} text={definitions ? definitions[defId] : undefined} />}
      </span>
    </th>
  );
}

function FacultySummaryStrip({ summary }) {
  const { turnaround, ungraded_backlog: backlog, class_average_per_exam: examRows, reach, exams_past_deadline_still_open: overdue } = summary;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
          Per-exam class average
        </h4>
        {examRows.length === 0 ? (
          <p className="text-sm text-slate-400">No graded exams in this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 dark:text-slate-400">
                  <th className="pr-3 py-1 font-medium">Exam</th>
                  <th className="pr-3 py-1 font-medium">Subject</th>
                  <th className="pr-3 py-1 font-medium">Average</th>
                  <th className="py-1 font-medium">Range</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {examRows.map((e) => (
                  <tr key={e.exam_id}>
                    <td className="pr-3 py-1.5 text-slate-800 dark:text-slate-200">{e.title}</td>
                    <td className="pr-3 py-1.5 text-slate-500 dark:text-slate-400">{e.subject || DASH}</td>
                    <td className="pr-3 py-1.5 text-slate-800 dark:text-slate-200">
                      {fmtPct(e.avg_pct)} <span className="text-slate-400">(n={e.n})</span>
                    </td>
                    <td className="py-1.5 text-slate-500 dark:text-slate-400">
                      {fmtPct(e.min_pct, 0)}&ndash;{fmtPct(e.max_pct, 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
          Exams still open past deadline
        </h4>
        {overdue.length === 0 ? (
          <p className="text-sm text-slate-400">None &mdash; nothing overdue.</p>
        ) : (
          <ul className="space-y-1.5">
            {overdue.map((o) => (
              <li key={o.exam_id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-800 dark:text-slate-200 truncate">{o.title}</span>
                <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 px-2 py-0.5 text-xs font-medium">
                  <AlertTriangle size={11} /> {fmtAge(o.days_past)} overdue
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
          Teaching reach
        </h4>
        <p className="text-sm text-slate-700 dark:text-slate-300">
          Assigned an exam to <strong>{reach.distinct_students_assigned}</strong> distinct student(s) this period.
        </p>
        <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">
          Currently teaches (in linked groups) <strong>{reach.distinct_students_in_groups}</strong> distinct student(s).
        </p>

        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-4 mb-2">
          Grading turnaround
        </h4>
        <p className="text-sm text-slate-700 dark:text-slate-300">
          Median {fmtMinutes(turnaround.median_minutes)} (n={turnaround.n} graded in range, by exam date).
        </p>
        <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">
          Oldest ungraded right now: {fmtAge(backlog.oldest_age_days)}.
        </p>

        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-4 mb-2">
          Ungraded backlog by status
        </h4>
        <p className="text-sm text-slate-700 dark:text-slate-300">
          {backlog.by_status.submitted || 0} explicitly submitted, {backlog.by_status.in_progress || 0} still in progress (student uploaded but never pressed Submit).
        </p>
      </div>
    </div>
  );
}

// Entry point into the §13 student report: clicking a student's name
// anywhere in this drill-down's roster opens their report. This is the
// natural path — the owner is already looking at a group when he wants to
// know about one student in it.
function StudentReportLink({ student }) {
  return (
    <Link
      to={`/admin/reports/students/${student.id}`}
      className="font-medium text-slate-900 dark:text-white hover:text-primary dark:hover:text-accent-cool hover:underline"
      data-testid={`open-student-report-${student.id}`}
    >
      {student.name}
    </Link>
  );
}

function RosterGroupCard({ group }) {
  const [formerOpen, setFormerOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700">
        <h4 className="font-medium text-slate-900 dark:text-white">{group.group_name}</h4>
        <p className="text-xs text-slate-400">{group.current_members.length} current member(s)</p>
      </div>
      {group.current_members.length === 0 ? (
        <div className="py-8 text-center text-slate-400 text-sm">No current members.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
              <th className="px-5 py-2.5 text-left font-medium text-slate-500">Name</th>
              <th className="px-5 py-2.5 text-left font-medium text-slate-500">Class</th>
              <th className="px-5 py-2.5 text-left font-medium text-slate-500">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {group.current_members.map((m) => (
              <tr key={m.id} data-testid={`roster-current-${m.id}`}>
                <td className="px-5 py-2.5">
                  <p><StudentReportLink student={m} /></p>
                  <p className="text-slate-500 dark:text-slate-400">{m.email}</p>
                </td>
                <td className="px-5 py-2.5 text-slate-500 dark:text-slate-400">{m.class_level ?? DASH}</td>
                <td className="px-5 py-2.5 text-slate-500 dark:text-slate-400 capitalize">{m.registration_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <button
        type="button"
        onClick={() => setFormerOpen((o) => !o)}
        className="flex w-full items-center gap-2 border-t border-slate-100 dark:border-slate-700 px-5 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
        data-testid={`toggle-former-${group.group_id}`}
      >
        {formerOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        Former members ({group.former_members.length})
      </button>
      {formerOpen && (
        group.former_members.length === 0 ? (
          <div className="px-5 py-4 text-center text-slate-400 text-sm border-t border-slate-100 dark:border-slate-700">
            No former members.
          </div>
        ) : (
          <table className="w-full text-sm border-t border-slate-100 dark:border-slate-700" data-testid={`former-members-table-${group.group_id}`}>
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                <th className="px-5 py-2.5 text-left font-medium text-slate-500">Name</th>
                <th className="px-5 py-2.5 text-left font-medium text-slate-500">Class</th>
                <th className="px-5 py-2.5 text-left font-medium text-slate-500">Left on</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {group.former_members.map((m) => (
                <tr key={m.id} className="text-slate-500 dark:text-slate-400" data-testid={`roster-former-${m.id}`}>
                  <td className="px-5 py-2.5">
                    <p><StudentReportLink student={m} /></p>
                    <p>{m.email}</p>
                  </td>
                  <td className="px-5 py-2.5">{m.class_level ?? DASH}</td>
                  <td className="px-5 py-2.5">{m.left_on ?? DASH}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}

export default function AdminFacultyDrilldownPage() {
  const { id: facultyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('group_name');
  const [sortDir, setSortDir] = useState('asc');

  const load = useCallback((params) => {
    setLoading(true);
    adminApi
      .getFacultyDrilldown(facultyId, params)
      .then(({ data: body }) => {
        setData(body);
        setDateFrom(body.date_range.start);
        setDateTo(body.date_range.end);
        setSearchParams({ date_from: body.date_range.start, date_to: body.date_range.end }, { replace: true });
      })
      .catch(() => toast.error('Failed to load the faculty drill-down'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facultyId]);

  useEffect(() => {
    const initial = {};
    const qFrom = searchParams.get('date_from');
    const qTo = searchParams.get('date_to');
    if (qFrom && qTo) {
      initial.date_from = qFrom;
      initial.date_to = qTo;
    }
    load(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facultyId]);

  const applyRange = () => {
    if (!dateFrom || !dateTo) return;
    load({ date_from: dateFrom, date_to: dateTo });
  };

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedGroupRows = useMemo(() => {
    if (!data) return [];
    return sortRows(data.group_rows, sortKey, sortDir);
  }, [data, sortKey, sortDir]);

  const backHref = `/admin/reports?date_from=${dateFrom}&date_to=${dateTo}`;

  return (
    <DashboardLayout title="Faculty Drill-down">
      <div className="animate-fade-in space-y-6">
        <Link
          to={backHref}
          className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent-cool transition-colors text-sm"
          data-testid="back-to-faculty-report"
        >
          <ArrowLeft size={16} /> Back to Faculty Report
        </Link>

        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-primary/10 dark:bg-accent-cool/10 p-2 text-primary dark:text-accent-cool">
            <BarChart3 size={20} />
          </div>
          <div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
              {loading || !data ? 'Loading…' : data.faculty.name}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Per-group breakdown for the selected date range. Read-only.
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">From</label>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || todayIso()}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool"
                data-testid="drilldown-date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">To</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool"
                data-testid="drilldown-date-to"
              />
            </div>
            <button
              type="button"
              onClick={applyRange}
              disabled={!dateFrom || !dateTo}
              className="px-4 py-2 rounded-md bg-primary dark:bg-accent-cool text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              data-testid="drilldown-apply-range"
            >
              Apply
            </button>
          </div>
        </div>

        {loading || !data ? (
          <div className="animate-pulse space-y-4">
            <div className="h-40 rounded-lg bg-slate-200 dark:bg-slate-700" />
            <div className="h-64 rounded-lg bg-slate-200 dark:bg-slate-700" />
          </div>
        ) : (
          <>
            {/* 1. Faculty summary strip (§10.3's relocated metrics) */}
            <FacultySummaryStrip summary={data.faculty_summary} />

            <HowToReadPanel definitions={data.metric_definitions} columns={data.columns} />

            {/* 2. Per-group table — its own horizontal-scroll container so
                the page body never scrolls sideways. */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
              {data.group_rows.length === 0 ? (
                <div className="py-16 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
                  <BarChart3 size={28} className="text-slate-300 dark:text-slate-600" />
                  No groups linked to this faculty.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[1300px]">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                        <SortableHeader id="group_name" label="Group" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        {data.columns.map((col) => (
                          <SortableHeader
                            key={col.id}
                            id={col.id}
                            label={col.label}
                            sortKey={sortKey}
                            sortDir={sortDir}
                            onSort={handleSort}
                            defId={col.id}
                            definitions={data.metric_definitions}
                          />
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {sortedGroupRows.map((row) => (
                        <GroupTableRow key={row.group_id} row={row} definitions={data.metric_definitions} slaDays={data.grading_sla_days} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* 3. Individuals row — only when non-empty (§12.8) */}
            {data.individual_row && (
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[1300px]">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                        <th className="px-4 py-3 text-left font-medium text-slate-500">Group</th>
                        {data.columns.map((col) => (
                          <Th key={col.id} defId={col.id} definitions={data.metric_definitions}>{col.label}</Th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <GroupTableRow row={data.individual_row} definitions={data.metric_definitions} slaDays={data.grading_sla_days} />
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 4. Roster panel */}
            <div className="space-y-4">
              <h3 className="text-lg font-heading font-bold text-slate-900 dark:text-white">Roster</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {data.roster.groups.map((g) => (
                  <RosterGroupCard key={g.group_id} group={g} />
                ))}
              </div>
              {data.roster.individuals.length > 0 && (
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700">
                    <h4 className="font-medium text-slate-900 dark:text-white">
                      {data.individual_row ? data.individual_row.group_name : 'Individually assigned students'}
                    </h4>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                        <th className="px-5 py-2.5 text-left font-medium text-slate-500">Name</th>
                        <th className="px-5 py-2.5 text-left font-medium text-slate-500">Class</th>
                        <th className="px-5 py-2.5 text-left font-medium text-slate-500">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {data.roster.individuals.map((m) => (
                        <tr key={m.id} data-testid={`roster-individual-${m.id}`}>
                          <td className="px-5 py-2.5">
                            <p><StudentReportLink student={m} /></p>
                            <p className="text-slate-500 dark:text-slate-400">{m.email}</p>
                          </td>
                          <td className="px-5 py-2.5 text-slate-500 dark:text-slate-400">{m.class_level ?? DASH}</td>
                          <td className="px-5 py-2.5 text-slate-500 dark:text-slate-400 capitalize">{m.registration_status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
